import { NgModule, Optional, SkipSelf, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { TokenRefreshInterceptor } from './interceptors/token-refresh.interceptor';
import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { GlobalErrorHandler } from './handlers/global-error.handler';

/**
 * Interceptor execution order (Angular runs them in registration order):
 *
 *  Request  →  AuthInterceptor        (attach Bearer token)
 *           →  TokenRefreshInterceptor (handle 401 → refresh → retry)
 *           →  LoaderInterceptor      (show/hide spinner)
 *           →  ErrorInterceptor       (toast for 403/0/5xx)
 *           →  HTTP call
 *
 *  Response ←  ErrorInterceptor
 *           ←  LoaderInterceptor
 *           ←  TokenRefreshInterceptor
 *           ←  AuthInterceptor
 */
@NgModule({
  providers: [
    MessageService,

    // 1. Attach Bearer token to every outgoing request
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor,        multi: true },

    // 2. Intercept 401 responses → call /auth/refresh → replay original request
    //    Must come AFTER AuthInterceptor so the retry already has a token,
    //    and BEFORE ErrorInterceptor so 401s never reach the toast handler.
    { provide: HTTP_INTERCEPTORS, useClass: TokenRefreshInterceptor, multi: true },

    // 3. Show / hide the global loading spinner
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor,      multi: true },

    // 4. Handle remaining errors (403, 0, 5xx) with toasts
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor,       multi: true },

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it only in AppModule.');
    }
  }
}
